import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogisticsStatusBadge, LogisticsTypeBadge } from '@/modules/logistics/components';
import { listLogisticsOrdersForReservation } from '@/modules/logistics/services';
import { buildLogisticsOrderNewUrl } from '@/modules/logistics/utils/logisticsUrl';
import type { LogisticsOrder } from '@/modules/logistics/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type ReservationLogisticsPanelProps = {
  reservationId: string;
};

export function ReservationLogisticsPanel({
  reservationId,
}: ReservationLogisticsPanelProps) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('logistics.read');
  const canWrite = hasPermission('logistics.write');
  const [orders, setOrders] = useState<LogisticsOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    void listLogisticsOrdersForReservation(reservationId)
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [reservationId, canRead]);

  if (!canRead) return null;

  return (
    <div className="inventory-panel">
      <div className="inventory-page-header">
        <h2 className="inventory-panel__subtitle">Logística asociada</h2>
        {canWrite ? (
          <div className="inventory-actions">
            <Link
              to={buildLogisticsOrderNewUrl({
                reservationId,
                type: 'delivery',
              })}
              className="auth-button auth-button--secondary inventory-page-header__btn"
            >
              + Entrega
            </Link>
            <Link
              to={buildLogisticsOrderNewUrl({
                reservationId,
                type: 'pickup',
              })}
              className="auth-button auth-button--secondary inventory-page-header__btn"
            >
              + Retiro
            </Link>
          </div>
        ) : null}
      </div>

      {loading ? <p className="auth-muted">Cargando órdenes…</p> : null}
      {!loading && orders.length === 0 ? (
        <p className="auth-muted">Sin órdenes logísticas para esta reserva.</p>
      ) : null}
      {!loading && orders.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>
                  <LogisticsTypeBadge type={o.type} />
                </td>
                <td>{o.scheduledDate}</td>
                <td>
                  <LogisticsStatusBadge status={o.status} />
                </td>
                <td>
                  <Link
                    to={`${modulePaths.logistics}/${o.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
